import React, { useEffect, useRef, useState } from "react";
import { FaUser, FaEnvelope, FaCommentAlt } from "react-icons/fa";
import "./ContactForm.scss";
import ButtonLink from "../../../Buttons/ButtonLink";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Cloudflare test key always passes, so the widget works on localhost.
const TURNSTILE_SITE_KEY =
  window.location.hostname === "localhost"
    ? "1x00000000000000000000AA"
    : "0x4AAAAAAFD3xb-Pa_AAm5Mu";
const TURNSTILE_SCRIPT =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    const renderWidget = () => {
      if (!turnstileRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: setToken,
        "expired-callback": () => setToken(""),
        "error-callback": () => setToken(""),
      });
    };

    let script = document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`);
    if (window.turnstile) {
      renderWidget();
    } else {
      if (!script) {
        script = document.createElement("script");
        script.src = TURNSTILE_SCRIPT;
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", renderWidget);
    }

    return () => {
      script?.removeEventListener("load", renderWidget);
      if (widgetIdRef.current !== null) {
        window.turnstile?.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  const resetTurnstile = () => {
    setToken("");
    if (widgetIdRef.current !== null) {
      window.turnstile?.reset(widgetIdRef.current);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidEmail(formData.email)) {
      alert("Please enter a valid email address.");
      return;
    }

    if (!token) {
      alert("Please wait for the verification to complete.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/.netlify/functions/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, token }),
      });

      if (response.ok) {
        alert("Message sent!");
        setFormData({ name: "", email: "", message: "" });
      } else {
        alert("Failed to send message. Please try again.");
      }
    } catch (error) {
      alert("Error sending message. Please try again.");
    } finally {
      resetTurnstile();
      setLoading(false);
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <div className="contact-form_input-wrapper">
        <FaUser className="contact-form_input-icon" />
        <input
          type="text"
          name="name"
          placeholder="Name"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>
      <div className="contact-form_input-wrapper">
        <FaEnvelope className="contact-form_input-icon" />
        <input
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          required
        />
      </div>
      <div className="contact-form_input-wrapper contact-form_input-wrapper--message">
        <FaCommentAlt className="contact-form_input-icon contact-form_input-icon--top" />
        <textarea
          name="message"
          placeholder="Message"
          value={formData.message}
          onChange={handleChange}
          required
        />
      </div>
      <div ref={turnstileRef} className="contact-form_turnstile" />
      <ButtonLink
        label={loading ? "Sending..." : "Send Message"}
        onClick={handleSubmit}
      />
    </form>
  );
}

export default ContactForm;
